import { create } from 'zustand';
import messageService from '../api/messageService';

const useMessageStore = create((set, get) => ({
  messages: [],
  loading: false,
  error: null,

  fetchMessages: async (params = {}) => {
    set({ loading: true, error: null });
    try {
      const response = await messageService.getAllMessages(params);
      set({ messages: response.data.messages || [], loading: false });
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch messages', loading: false });
    }
  },

  sendMessage: async (messageData) => {
    try {
      const response = await messageService.sendMessage(messageData);
      set((state) => ({ messages: [...state.messages, response.data.message] }));
      return { success: true, data: response.data.message };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to send message' };
    }
  },

  updateMessageStatus: async (messageId, newStatus) => {
    try {
      const response = await messageService.updateMessageStatus(messageId, newStatus);
      set((state) => ({ messages: state.messages.map(msg => msg._id === messageId ? response.data.message : msg) }));
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to update status' };
    }
  },

  replyToMessage: async (messageId, replyText) => {
    try {
      const response = await messageService.replyToMessage(messageId, replyText);
      set((state) => ({ messages: state.messages.map(msg => msg._id === messageId ? response.data.message : msg) }));
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to send reply' };
    }
  },

  toggleStar: async (messageId) => {
    try {
      const response = await messageService.toggleStar(messageId);
      set((state) => ({ messages: state.messages.map(msg => msg._id === messageId ? response.data.message : msg) }));
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to toggle star' };
    }
  },

  deleteMessage: async (messageId) => {
    try {
      await messageService.deleteMessage(messageId);
      set((state) => ({ messages: state.messages.filter(msg => msg._id !== messageId) }));
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to delete message' };
    }
  },

  getMessagesByStatus: (status) => {
    const { messages } = get();
    if (status === 'all') return messages;
    return messages.filter(msg => msg.status === status);
  },

  getStarredMessages: () => {
    const { messages } = get();
    return messages.filter(msg => msg.isStarred);
  },

  searchMessages: (searchTerm) => {
    const { messages } = get();
    if (!searchTerm) return messages;
    const term = searchTerm.toLowerCase();
    return messages.filter(msg => msg.name?.toLowerCase().includes(term) || msg.email?.toLowerCase().includes(term) || msg.subject?.toLowerCase().includes(term) || msg.message?.toLowerCase().includes(term));
  },

  getStats: () => {
    const { messages } = get();
    const total = messages.length;
    const unread = messages.filter(msg => msg.status === 'New').length;
    const starred = messages.filter(msg => msg.isStarred).length;
    const resolved = messages.filter(msg => msg.status === 'Resolved').length;
    return { total, unread, starred, resolved };
  },

  getStatistics: () => {
    const { messages } = get();
    const total = messages.length;
    const newCount = messages.filter(msg => msg.status === 'New').length;
    const inProgress = messages.filter(msg => msg.status === 'In Progress').length;
    const resolved = messages.filter(msg => msg.status === 'Resolved').length;
    const closed = messages.filter(msg => msg.status === 'Closed').length;
    const pending = newCount + inProgress; // Pending = New + In Progress
    const replied = messages.filter(msg => msg.replied === true || msg.status === 'Resolved' || msg.status === 'Closed').length;
    const highPriority = messages.filter(msg => msg.priority === 'High' || msg.priority === 'Urgent').length;
    
    const byType = {};
    messages.forEach(msg => {
      byType[msg.type] = (byType[msg.type] || 0) + 1;
    });
    
    const byPriority = {
      Low: messages.filter(msg => msg.priority === 'Low').length,
      Medium: messages.filter(msg => msg.priority === 'Medium').length,
      High: messages.filter(msg => msg.priority === 'High').length,
      Urgent: messages.filter(msg => msg.priority === 'Urgent').length
    };
    
    return {
      total,
      new: newCount,
      pending,
      inProgress,
      resolved,
      closed,
      replied,
      highPriority,
      byType,
      byPriority
    };
  },

  getFilteredMessages: ({ searchTerm, status, type, priority } = {}) => {
    let { messages } = get();
    
    // Filter by search term
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      messages = messages.filter(msg => 
        msg.name?.toLowerCase().includes(term) || 
        msg.email?.toLowerCase().includes(term) || 
        msg.subject?.toLowerCase().includes(term) || 
        msg.message?.toLowerCase().includes(term)
      );
    }
    
    // Filter by status
    if (status) {
      messages = messages.filter(msg => msg.status === status);
    }
    
    // Filter by type
    if (type) {
      messages = messages.filter(msg => msg.type === type);
    }
    
    // Filter by priority
    if (priority) {
      messages = messages.filter(msg => msg.priority === priority);
    }
    
    return messages;
  }
}));

export default useMessageStore;
